// Zone de dépôt d'une question de type `fichier` de la page publique (contrat : ZoneDepotProps dans ./props.ts).
// Rectangle pointillé cliquable (charte verte des tokens de l'onboarding) + <input type=file hidden> + glisser-déposer.
// L'upload part IMMÉDIATEMENT à la sélection, sans sauvegarde préalable des valeurs (règle B : le backend ne
// contrôle pas la visibilité ; la purge à la soumission retire les fichiers des questions devenues invisibles).
// Contrôles côté client AVANT tout appel : extension ∈ question.accept (défaut EXTENSIONS_AUTORISEES, même règle
// que path.extname côté backend) et taille ≤ 20 Mo → onErreur(message) sans requête. Pendant l'envoi :
// LinearProgress (pourcentage d'uploaderFichier) et zone désactivée. Plusieurs fichiers sélectionnés d'un coup
// (max_fichiers > 1) sont envoyés l'un après l'autre ; onChange est appelé après CHAQUE 201 avec la liste augmentée
// et l'état renvoyé (le parent adopte version_reponses). Erreur backend (400 extension, 409 max atteint, 413, 410,
// 503, réseau…) → onErreur(erreur.message, erreur) et arrêt de la file. « Retirer » → supprimerFichier → onChange
// (liste sans le fichier, état). Aucun état de fichiers ici : la liste appartient au parent. Chaque fichier déposé
// est montré par une VignetteFichier (miniature réelle pour une image, icône typée sinon), pas seulement son nom.
import React, { useRef, useState } from 'react';
import { Box, IconButton, LinearProgress, List, ListItem, Typography, alpha } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import type { ZoneDepotProps } from './props';
import type { ErreurPublique, EtatEcriture, FichierReponse, Question, ReponseUpload } from './types';
import { EXTENSIONS_AUTORISEES, LIMITES_FICHIER } from './types';
import { tailleLisible } from './libelles';
import { supprimerFichier, uploaderFichier } from './formulairesPublicsServices';
import VignetteFichier from './VignetteFichier';
import { RAYON, VERT } from '../onboarding/tokens';

const TAILLE_MAX_MO = Math.round(LIMITES_FICHIER.tailleMax / (1024 * 1024));

// Extensions acceptées par la question (minuscules, restreintes à la liste blanche) ; défaut : la liste entière
const extensionsAcceptees = (q: Question): string[] => {
  const brutes: string[] = q.accept && q.accept.length ? q.accept : EXTENSIONS_AUTORISEES;
  const liste = brutes.map((x) => String(x).toLowerCase()).filter((x) => (EXTENSIONS_AUTORISEES as string[]).indexOf(x) >= 0);
  return liste.length ? liste : [...EXTENSIONS_AUTORISEES];
};

// Extension d'un nom de fichier, en minuscules, sans le point ('' si aucune) — même règle que path.extname : un nom
// commençant par un point sans autre point (« .htaccess ») n'a pas d'extension
const extensionDe = (nom: string): string => {
  const base = String(nom || '');
  const i = base.lastIndexOf('.');
  return i <= 0 ? '' : base.slice(i + 1).toLowerCase();
};

// « PDF, JPG, PNG, HEIC, DOC, DOCX » adapté aux extensions de la question (jpeg fusionné dans JPG)
const libelleFormats = (accept: string[]): string => {
  const libelles: string[] = [];
  accept.forEach((ext) => {
    const l = ext === 'jpeg' ? (accept.indexOf('jpg') >= 0 ? 'JPG' : 'JPEG') : ext.toUpperCase();
    if (libelles.indexOf(l) < 0) libelles.push(l);
  });
  return libelles.join(', ');
};

// Erreur rejetée par le service (toujours normalisée) ; garde-fou si autre chose remonte malgré tout
const erreurDe = (e: unknown): ErreurPublique => {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    const err = e as { status?: unknown; message: string };
    return typeof err.status === 'number' || err.status === null
      ? (e as ErreurPublique)
      : { status: null, message: err.message };
  }
  return { status: null, message: 'Erreur inattendue pendant le transfert' };
};

interface Transfert {
  nom: string;   // nom du fichier en cours d'envoi
  pct: number;   // 0..100
  rang: number;  // 1..total (plusieurs fichiers sélectionnés d'un coup)
  total: number;
}

const ZoneDepot: React.FC<ZoneDepotProps> = ({ jeton, question: q, fichiers, disabled = false, onChange, onErreur, onTransfert }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // dragenter / dragleave se déclenchent aussi au passage sur les enfants : sans compteur, le cadre clignote
  const compteurDrag = useRef(0);
  const [survol, setSurvol] = useState(false);
  const [transfert, setTransfert] = useState<Transfert | null>(null);
  const [retrait, setRetrait] = useState<string | null>(null); // nom du fichier en cours de retrait

  const accept = extensionsAcceptees(q);
  const formats = libelleFormats(accept);
  const max = q.max_fichiers || 1;
  const plein = fichiers.length >= max;
  const occupe = transfert !== null || retrait !== null;
  const inactif = disabled || plein || occupe;

  // Contrôle client puis envoi séquentiel des fichiers retenus
  const deposer = async (selection: File[]) => {
    if (inactif || !selection.length) return;
    const retenus: File[] = [];
    for (const f of selection) {
      if (fichiers.length + retenus.length >= max) {
        onErreur(`${max} fichier${max > 1 ? 's' : ''} maximum pour cette question`);
        break;
      }
      const ext = extensionDe(f.name);
      if (!ext || accept.indexOf(ext) < 0) {
        onErreur(`« ${f.name} » : type de fichier non accepté (${formats})`);
        continue;
      }
      if (f.size > LIMITES_FICHIER.tailleMax) {
        onErreur(`« ${f.name} » : fichier trop volumineux (${tailleLisible(f.size)}, ${TAILLE_MAX_MO} Mo max)`);
        continue;
      }
      retenus.push(f);
    }
    if (!retenus.length) return;
    if (onTransfert) onTransfert(true);
    let liste = fichiers;
    for (let i = 0; i < retenus.length; i += 1) {
      const f = retenus[i];
      setTransfert({ nom: f.name, pct: 0, rang: i + 1, total: retenus.length });
      let r: ReponseUpload;
      try {
        r = await uploaderFichier(jeton, q.id, f, (pct) => setTransfert((t) => (t ? { ...t, pct } : t)));
      } catch (e) {
        setTransfert(null);
        if (onTransfert) onTransfert(false);
        const erreur = erreurDe(e);
        onErreur(erreur.message, erreur);
        return;
      }
      liste = [...liste, r.fichier];
      onChange(liste, r);
    }
    setTransfert(null);
    if (onTransfert) onTransfert(false);
  };

  const retirer = async (f: FichierReponse) => {
    if (disabled || occupe) return;
    setRetrait(f.nom);
    if (onTransfert) onTransfert(true);
    let etat: EtatEcriture;
    try {
      etat = await supprimerFichier(jeton, f.nom);
    } catch (e) {
      setRetrait(null);
      if (onTransfert) onTransfert(false);
      const erreur = erreurDe(e);
      onErreur(erreur.message, erreur);
      return;
    }
    setRetrait(null);
    if (onTransfert) onTransfert(false);
    onChange(fichiers.filter((x) => x.nom !== f.nom), etat);
  };

  const ouvrirSelecteur = () => {
    if (!inactif && inputRef.current) inputRef.current.click();
  };

  const surChangementInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selection = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = ''; // permet de resélectionner le même fichier après un refus ou un retrait
    deposer(selection);
  };

  // Zone désactivée : le glisser-déposer reste neutralisé (sinon le navigateur ouvrirait le fichier à la place de
  // la page du formulaire), le dépôt est simplement ignoré
  const gestionnairesDrag = inactif
    ? {
        onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'none'; },
        onDrop: (e: React.DragEvent) => { e.preventDefault(); },
      }
    : {
        onDragEnter: (e: React.DragEvent) => {
          e.preventDefault();
          compteurDrag.current += 1;
          setSurvol(true);
        },
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: (e: React.DragEvent) => {
          e.preventDefault();
          compteurDrag.current -= 1;
          if (compteurDrag.current <= 0) {
            compteurDrag.current = 0;
            setSurvol(false);
          }
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          compteurDrag.current = 0;
          setSurvol(false);
          deposer(e.dataTransfer.files ? Array.from(e.dataTransfer.files) : []);
        },
      };

  let consigne: string;
  if (transfert) consigne = `Envoi de « ${transfert.nom} »${transfert.total > 1 ? ` (${transfert.rang}/${transfert.total})` : ''}… ${transfert.pct} %`;
  else if (plein) consigne = max > 1 ? `Nombre maximum de fichiers atteint (${max})` : 'Fichier joint';
  else consigne = max > 1 ? 'Appuyez ou glissez vos fichiers ici' : 'Appuyez ou glissez votre fichier ici';

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {fichiers.length > 0 && (
        <List dense disablePadding sx={{ mb: 1 }}>
          {fichiers.map((f) => (
            <ListItem
              key={f.nom}
              disableGutters
              sx={{ pr: 5, py: 0.75, alignItems: 'center' }}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={`Retirer ${f.nom_original || f.nom}`}
                  disabled={disabled || occupe}
                  onClick={() => retirer(f)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <VignetteFichier
                jeton={jeton}
                fichier={f}
                secondaire={retrait === f.nom ? 'Retrait en cours…' : undefined}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Box
        role="button"
        tabIndex={inactif ? -1 : 0}
        aria-disabled={inactif}
        onClick={ouvrirSelecteur}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            ouvrirSelecteur();
          }
        }}
        {...gestionnairesDrag}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          px: 2,
          py: 2.5,
          borderRadius: RAYON,
          border: '2px dashed',
          borderColor: survol ? VERT : alpha(VERT, 0.45),
          backgroundColor: survol ? alpha(VERT, 0.1) : alpha(VERT, 0.03),
          color: 'text.primary',
          cursor: inactif ? 'not-allowed' : 'pointer',
          opacity: disabled || plein ? 0.6 : 1,
          textAlign: 'center',
          outline: 'none',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          '&:hover': inactif ? undefined : { borderColor: VERT, backgroundColor: alpha(VERT, 0.06) },
          '&:focus-visible': { borderColor: VERT, boxShadow: `0 0 0 3px ${alpha(VERT, 0.25)}` },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          aria-label={`Sélection de fichier — ${q.libelle}`}
          accept={accept.map((ext) => `.${ext}`).join(',')}
          multiple={max > 1}
          disabled={inactif}
          onChange={surChangementInput}
        />
        <CloudUploadOutlinedIcon sx={{ fontSize: 34, color: VERT }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{consigne}</Typography>
        {transfert ? (
          <LinearProgress
            variant="determinate"
            value={transfert.pct}
            aria-label="Progression du transfert"
            sx={{ width: '100%', borderRadius: 1, '& .MuiLinearProgress-bar': { backgroundColor: VERT } }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {`${formats} — ${TAILLE_MAX_MO} Mo max`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ZoneDepot;
